import StatCards from './ui/StatCards';
import ApplicationJourney, { ApplicationFlags, InstitutionalFlag, roleLabel } from './ApplicationJourney';
import fetch from '../utils/authenticatedFetch';
import ModalLayer from './ModalLayer';
// src/components/ApplicationPage.tsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './ApplicationPage.css';
import './Dashboard.css';
import { useAuth } from 'contexts/AuthContext';

const API_BASE = '';
const FILE_BASE = 'https://ethics.ri.msu.ac.zw';

/* =========================
   Types
========================= */
type ResumeStep = 1 | 2 | 3 | 4 | 5;
type SortKey = 'title' | 'applicant' | 'status' | 'date' | 'progress';

interface AppDocument {
  id: number;
  documentType: string;
  originalFileName?: string | null;
  filePath?: string | null;
  downloadUrl?: string | null;
  fileSize?: number | null;
  contentType?: string | null;
  uploadedByUserId?: number | null;
  uploadedByUserName?: string | null;
  workflowStageCode?: string | null;
  workflowStageName?: string | null;
  applicationSection?: string | null;
  documentSource?: string | null;
  logicalDocumentKey?: string | null;
  versionNumber?: number | null;
  active?: boolean | null;
  uploadedAt?: string | null;
  revisionNumber?: number | null;
  replacedDocumentId?: number | null;
}

interface AppComment {
  id: number;
  comment?: string | null;
  nextApproverId?: number | null;
  nextApproverName?: string | null;
  createdByUserId?: number | null;
  createdByUserName?: string | null;
  status?: string | null;
  createdAt?: string | null;
  attachmentType?: string | null;
  originalFileName?: string | null;
  filePath?: string | null;
}

interface ApplicationDetails {
  applicantInstitutionalRole?: string;
  reviewOutcome?: string;
  flags?: InstitutionalFlag[];
  id: number;
  researchTitle?: string | null;
  researchDescription?: string | null;
  proposalDefended?: boolean | null;
  fieldwork?: boolean | null;
  relevantExperience?: string | null;
  coResearchersDetails?: string | null;
  academicQualifications?: string | null;
  fundingOfResearch?: string | null;
  researchDesign?: unknown;
  researchTool?: unknown;
  stageOneSubmittedAt?: string | null;
  categoryOfParticipants?: string | null;
  levelOfSensitivity?: string | null;
  researchLocation?: string | null;
  proposedFieldwork?: string | null;
  fieldworkDetails?: string | null;
  stageFourSubmittedAt?: string | null;
  declarationAccepted?: boolean | null;
  status?: string | null;
  applicantId?: number | null;
  applicantName?: string | null;
  applicantSurname?: string | null;
  applicantFacultyCode?: string | null;
  applicantDepartmentCode?: string | null;
  applicationType?: string | null;
  draftReference?: string | null;
  savedStep?: number | null;
  createdAt?: string | null;
  draftLastUpdatedAt?: string | null;
  completionPercentage?: number | null;
  documents?: AppDocument[];
  comments?: AppComment[];
  ethicalClearanceOriginalName?: string | null;
  ethicalClearancePath?: string | null;
  approvalLetterOriginalName?: string | null;
  approvalLetterPath?: string | null;
  editableByApplicant?: boolean | null;
  revisionsRequired?: boolean | null;
  returnedWorkflowStageId?: number | null;
  returnedWorkflowStageCode?: string | null;
  returnedWorkflowStageName?: string | null;
  currentWorkflowStageId?: number | null;
  currentWorkflowStageCode?: string | null;
  currentWorkflowAssignmentId?: number | null;
  currentStageEnteredAt?: string | null;
  currentStageDueAt?: string | null;
  currentStageCompletedAt?: string | null;
  currentStageTimeStatus?: string | null;
  revisionDecisionAt?: string | null;
  revisionRemarks?: string | null;
  revisionDecisionAttachmentName?: string | null;
  revisionDecisionAttachmentPath?: string | null;
  revisionNumber?: number | null;
  returnedForCorrectionAt?: string | null;
  resubmittedAt?: string | null;
  resubmissions?: Array<{
    id: number;
    revisionNumber?: number | null;
    submittedByUserName?: string | null;
    submittedAt?: string | null;
    previousStatus?: string | null;
    newStatus?: string | null;
    remarks?: string | null;
  }>;
}

/* =========================
   Helpers
========================= */
function getFileNameFromPath(filePath: string | undefined | null): string {
  if (!filePath) return '';
  return filePath.split(/[/\\]/).pop() || '';
}

function trimLeadingSlashes(path: string): string {
  return path.replace(/^\/+/, '');
}

function isGeneratedApprovalPath(path: string | undefined | null): boolean {
  if (!path) return false;
  const normalized = trimLeadingSlashes(path).toLowerCase();
  return normalized.startsWith('upload/approvaldocuments/');
}

function uniqueStrings(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean)));
}

function buildNormalDocumentCandidates(
  filePath: string | undefined | null,
  applicationId?: number | string
): string[] {
  const fileName = getFileNameFromPath(filePath);
  if (!fileName) return [];

  const encodedName = encodeURIComponent(fileName);
  const urls: string[] = [];

  if (applicationId !== undefined && applicationId !== null) {
    urls.push(`${FILE_BASE}/uploads/${encodeURIComponent(String(applicationId))}/${encodedName}`);
    urls.push(`/uploads/${encodeURIComponent(String(applicationId))}/${encodedName}`);
  }

  urls.push(`${FILE_BASE}/uploads/${encodedName}`);
  urls.push(`/uploads/${encodedName}`);

  return uniqueStrings(urls);
}

function buildGeneratedDocumentCandidates(filePath: string | undefined | null): string[] {
  if (!filePath) return [];

  const normalized = trimLeadingSlashes(filePath);
  const fileName = getFileNameFromPath(filePath);
  const encodedName = encodeURIComponent(fileName);

  const urls: string[] = [
    `${FILE_BASE}/${normalized}`,
    `/${normalized}`,
    `${FILE_BASE}/upload/approvaldocuments/${encodedName}`,
    `/upload/approvaldocuments/${encodedName}`,
  ];

  return uniqueStrings(urls);
}

function buildFileCandidates(
  filePath: string | undefined | null,
  applicationId?: number | string
): string[] {
  if (!filePath) return [];
  if (isGeneratedApprovalPath(filePath)) {
    return buildGeneratedDocumentCandidates(filePath);
  }
  return buildNormalDocumentCandidates(filePath, applicationId);
}

function buildWorkflowAttachmentUrls(
  comment: AppComment,
  applicationId: number,
  userId?: number | string
): string[] {
  const urls: string[] = [];
  if (userId !== undefined && userId !== null) {
    urls.push(
      `${API_BASE}/v1/api/documents/applications/${encodeURIComponent(String(applicationId))}` +
      `/workflow-attachments/${encodeURIComponent(String(comment.id))}/download?userId=${encodeURIComponent(String(userId))}`
    );
  }
  urls.push(...buildFileCandidates(comment.filePath || comment.originalFileName, applicationId));
  return uniqueStrings(urls);
}

async function fetchFileBlob(url: string): Promise<Blob> {
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'include',
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch document (${res.status})`);
  }

  const ct = (res.headers.get('content-type') || '').toLowerCase();
  if (ct.includes('text/html')) {
    throw new Error('File URL returned HTML, not a file stream.');
  }

  return await res.blob();
}

async function openBlob(blob: Blob, fileName?: string, mode: 'open' | 'download' = 'open') {
  const blobUrl = window.URL.createObjectURL(blob);

  if (mode === 'open') {
    window.open(blobUrl, '_blank', 'noopener,noreferrer');
  } else {
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName || 'document';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
}

async function openWithCandidates(
  urls: string[],
  fileName?: string,
  mode: 'open' | 'download' = 'open'
) {
  if (!urls.length) {
    throw new Error('Document path is missing or invalid.');
  }

  let lastError: unknown;

  for (const url of urls) {
    try {
      const blob = await fetchFileBlob(url);
      await openBlob(blob, fileName, mode);
      return;
    } catch (e) {
      lastError = e;
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Unable to open document.');
}

function parseJsonObject(value: unknown): Record<string, any> {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, any>;
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, any>;
      }
    } catch {
      return {};
    }
  }
  return {};
}

function toTitleCase(input: string): string {
  return input
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function humanizeBool(value: boolean | null | undefined): string {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return '—';
}

function safeText(value: unknown): string {
  if (value === null || value === undefined) return '—';
  const str = String(value).trim();
  return str ? str : '—';
}

function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function formatDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
}

function getSelectedFlags(value: unknown): string[] {
  const parsed = parseJsonObject(value);
  return Object.entries(parsed)
    .filter(([, v]) => v === true)
    .map(([k]) => toTitleCase(k));
}

function formatDocumentType(type?: string | null): string {
  if (!type) return '—';
  return toTitleCase(type);
}

function formatFileSize(bytes?: number | null): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const mib = bytes / 1024 / 1024;
  if (mib >= 1) return `${mib.toFixed(mib >= 10 ? 1 : 2)} MiB`;
  return `${(bytes / 1024).toFixed(1)} KiB`;
}

function documentKey(doc: AppDocument): string {
  return `${doc.documentSource || 'DOCUMENT'}-${doc.id}-${doc.logicalDocumentKey || doc.documentType}`;
}

function buildDocumentUrls(doc: AppDocument, applicationId: number, userId?: number | string): string[] {
  const urls: string[] = [];
  if (doc.downloadUrl && userId !== undefined && userId !== null) {
    const separator = doc.downloadUrl.includes('?') ? '&' : '?';
    urls.push(`${doc.downloadUrl}${separator}userId=${encodeURIComponent(String(userId))}`);
  }
  urls.push(...buildFileCandidates(doc.filePath, applicationId));
  return uniqueStrings(urls);
}

const DocumentList: React.FC<{
  documents?: AppDocument[];
  applicationId: number;
  userId?: number | string;
  requireSequentialRead?: boolean;
  readStates?: boolean[];
  onToggleRead?: (index: number) => void;
}> = ({ documents = [], applicationId, userId, requireSequentialRead = false, readStates = [], onToggleRead }) => {
  const [query, setQuery] = useState('');
  const [openingKey, setOpeningKey] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const filteredDocuments = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return documents;
    return documents.filter((doc) => [
      doc.documentType,
      doc.originalFileName,
      doc.uploadedByUserName,
      doc.workflowStageName,
      doc.applicationSection,
      doc.documentSource,
    ].some((value) => (value || '').toLowerCase().includes(q)));
  }, [documents, query]);

  if (!documents.length) {
    return <p className="apg-muted">No documents found.</p>;
  }

  return (
    <div className="apg-document-list">
      <div className="apg-document-toolbar">
        <input
          type="search"
          className="apg-document-search"
          placeholder="Search documents"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search documents"
        />
      </div>
      {downloadError ? <div className="apg-inline-error">{downloadError}</div> : null}
      {filteredDocuments.length ? (
        <div className="apg-table-scroll" tabIndex={0} aria-label="Documents table">
          <table className="app-modal-docs-table apg-documents-table">
            <thead>
              <tr>
                <th>Document</th>
                <th>Size</th>
                <th>Uploaded</th>
                <th>Version</th>
                <th>Stage / Section</th>
                {requireSequentialRead ? <th>Read?</th> : null}
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredDocuments.map((doc) => {
                const originalIndex = documents.findIndex((item) => documentKey(item) === documentKey(doc));
                const disabled = requireSequentialRead && originalIndex > 0 && !readStates[originalIndex - 1];
                const read = !requireSequentialRead || !!readStates[originalIndex];
                const label =
                  doc.originalFileName ||
                  getFileNameFromPath(doc.filePath) ||
                  doc.documentType ||
                  'Document';
                const urls = buildDocumentUrls(doc, applicationId, userId);
                const key = documentKey(doc);

                return (
                  <tr key={key} className={disabled ? 'faded' : ''}>
                    <td className="apg-document-cell">
                      <strong className="apg-document-title">{formatDocumentType(doc.documentType)}</strong>
                      <span className="apg-document-filename">{label}</span>
                    </td>
                    <td className="apg-document-size">{formatFileSize(doc.fileSize)}</td>
                    <td>
                      <span className="apg-document-uploader">{safeText(doc.uploadedByUserName)}</span>
                      <span className="apg-document-date">{formatDateTime(doc.uploadedAt)}</span>
                    </td>
                    <td className="apg-document-version">
                      <span className="apg-version-badge">v{doc.versionNumber ?? 1}</span>
                      {doc.active === false ? (
                        <span className="apg-document-status">Previous</span>
                      ) : (
                        <span className="apg-document-status apg-document-status-current">Current</span>
                      )}
                    </td>
                    <td>{safeText(doc.workflowStageName || doc.applicationSection || doc.documentSource).replace(/_/g, ' ')}</td>
                    {requireSequentialRead ? (
                      <td>
                        <label className="switch">
                          <input
                            type="checkbox"
                            disabled={disabled}
                            checked={!!readStates[originalIndex]}
                            onChange={() => onToggleRead?.(originalIndex)}
                          />
                          <span className="slider round"></span>
                        </label>
                      </td>
                    ) : null}
                    <td>
                      <button
                        type="button"
                        className="app-download-btn"
                        disabled={!read || !urls.length || openingKey === key}
                        onClick={async () => {
                          setOpeningKey(key);
                          setDownloadError(null);
                          try {
                            await openWithCandidates(urls, label, 'open');
                          } catch (e) {
                            setDownloadError(e instanceof Error ? e.message : 'Unable to open document.');
                          } finally {
                            setOpeningKey(null);
                          }
                        }}
                      >
                        {openingKey === key ? 'Opening...' : 'View'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="apg-muted">No documents match your search.</p>
      )}
    </div>
  );
};

/* =========================
   Resume-step helpers
========================= */
const hasValue = (val: unknown): boolean => {
  if (val === null || val === undefined) return false;
  if (typeof val === 'string') return val.trim().length > 0;
  if (typeof val === 'number') return true;
  if (typeof val === 'boolean') return true;
  if (Array.isArray(val)) return val.length > 0;
  if (typeof val === 'object') return Object.keys(val as object).length > 0;
  return true;
};

const isStep2Complete = (app: ApplicationDetails): boolean => {
  const requiredFields: (keyof ApplicationDetails)[] = [
    'researchTitle',
    'researchDescription',
    'proposalDefended',
    'fieldwork',
    'relevantExperience',
    'coResearchersDetails',
    'academicQualifications',
    'fundingOfResearch',
    'researchDesign',
    'researchTool',
  ];
  return requiredFields.every((field) => hasValue(app[field]));
};

const isStep3Complete = (app: ApplicationDetails): boolean => {
  const requiredFields: (keyof ApplicationDetails)[] = [
    'categoryOfParticipants',
    'levelOfSensitivity',
    'researchLocation',
    'proposedFieldwork',
    'fieldworkDetails',
  ];
  return requiredFields.every((field) => hasValue(app[field]));
};

const isStep4Complete = (app: ApplicationDetails): boolean => {
  const requiredDocs = [
    'consent_form',
    'information_sheet',
    'research_protocol',
    'data_collection_tool',
    'participant_consent_letter',
  ];

  const docs = Array.isArray(app.documents) ? app.documents : [];

  return requiredDocs.every((docType) =>
    docs.some(
      (d) =>
        typeof d.documentType === 'string' &&
        d.documentType.toLowerCase() === docType.toLowerCase()
    )
  );
};

const isStep5Complete = (app: ApplicationDetails): boolean => {
  return app.declarationAccepted === true;
};

export const determineResumeStep = (app: ApplicationDetails): ResumeStep => {
  if (!isStep2Complete(app)) return 2;
  if (!isStep3Complete(app)) return 3;
  if (!isStep4Complete(app)) return 4;
  if (!isStep5Complete(app)) return 5;
  return 5;
};

/* -------------------------------
   Filter + Status helpers
-------------------------------- */
const statusOptions = [
  { label: 'All', value: '' },
  { label: 'Draft', value: 'DRAFT' },
  { label: 'Submitted', value: 'SUBMITTED' },
  { label: 'Department Recommendation', value: 'RECOMMENDED' },
  { label: 'Faculty Recommendation', value: 'SEND_FOR_REVIEW' },
  { label: 'Research Chair Recommendation', value: 'REVIEWED' },
  { label: 'Directorate Approval', value: 'SEND_TO_FINAL' },
  { label: 'Revisions Required', value: 'NOT_RECOMMENDED' },
  { label: 'Accepted', value: 'ACCEPTED' },
  { label: 'Declined', value: 'DECLINED' },
];

const statusBadgeClass = (status: string | null | undefined): string => {
  const s = (status || '').toUpperCase();
  if (s === 'ACCEPTED') return 'apg-badge apg-badge-accepted';
  if (s === 'DECLINED') return 'apg-badge apg-badge-declined';
  if (s === 'NOT_RECOMMENDED' || s === 'REJECTED') return 'apg-badge apg-badge-declined';
  if (s === 'DRAFT') return 'apg-badge apg-badge-draft';
  if (s === 'SUBMITTED') return 'apg-badge apg-badge-submitted';
  if (s === 'RECOMMENDED') return 'apg-badge apg-badge-recommended';
  if (s === 'SEND_FOR_REVIEW') return 'apg-badge apg-badge-faculty';
  if (s === 'REVIEWED') return 'apg-badge apg-badge-chair';
  if (s === 'SEND_TO_FINAL') return 'apg-badge apg-badge-directorate';
  return 'apg-badge';
};

const statusBadgeLabel = (status: string | null | undefined): string => {
  const s = (status || '').toUpperCase();
  switch (s) {
    case 'DRAFT':
      return 'Draft';
    case 'SUBMITTED':
      return 'Submitted';
    case 'RECOMMENDED':
      return 'Dept. Recommendation';
    case 'SEND_FOR_REVIEW':
      return 'Faculty Recommendation';
    case 'REVIEWED':
      return 'Research Chair Recommendation';
    case 'SEND_TO_FINAL':
      return 'Executive Director Confirmation';
    case 'NOT_RECOMMENDED':
      return 'Revisions Required';
    case 'REVISION_REQUIRED':
      return 'Revisions Required';
    case 'REJECTED':
      return 'Not Recommended';
    case 'ACCEPTED':
      return 'Accepted';
    case 'DECLINED':
      return 'Declined';
    default:
      return s.replace(/_/g, ' ') || 'N/A';
  }
};

const progressStages: Record<string, number> = {
  SUBMITTED: 20,
  RECOMMENDED: 40,
  SEND_FOR_REVIEW: 60,
  REVIEWED: 80,
  SEND_TO_FINAL: 90,
  NOT_RECOMMENDED: 0,
  ACCEPTED: 100,
};

const computeProgress = (status: string | null | undefined): number =>
  progressStages[(status || '').toUpperCase()] || 0;

const displayedProgress = (app: ApplicationDetails): number => {
  const status = (app.status || '').toUpperCase();
  if (!status || status === 'DRAFT') {
    return app.completionPercentage ?? 0;
  }
  return computeProgress(status);
};

/* -------------------------------
   Debounce hook
-------------------------------- */
function useDebounced<T>(value: T, delay = 250): T {
  const [v, setV] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return v;
}

/* -------------------------------
   Viewport-aware page size
-------------------------------- */
function useAutoPerPage(defaultPer = 10) {
  const [auto, setAuto] = useState(defaultPer);

  useEffect(() => {
    const calc = () => {
      const h = Math.max(480, window.innerHeight);
      const rows = Math.max(6, Math.min(50, Math.floor((h - 260) / 56)));
      setAuto(rows);
    };

    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, []);

  return auto;
}

/* -------------------------------
   Linear Progress
-------------------------------- */
const LinearProgress: React.FC<{ value: number; compact?: boolean; label?: boolean }> = ({
  value,
  compact,
  label,
}) => {
  const pct = Math.max(0, Math.min(100, value));

  return (
    <div
      className={`apg-progress ${compact ? 'apg-progress-compact' : ''}`}
      aria-label={`Progress: ${pct}%`}
    >
      <div className="apg-progress-track">
        <div
          className="apg-progress-bar"
          style={{ width: `${pct}%`, transition: 'width .6s cubic-bezier(.4,0,.2,1)' }}
        />
      </div>
      {label && <span className="apg-progress-label">{pct}%</span>}
    </div>
  );
};

/* -------------------------------
   Detail row
-------------------------------- */
const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="apg-detail-row">
    <div className="apg-detail-label">{label}</div>
    <div className="apg-detail-value">{value ?? '—'}</div>
  </div>
);

/* -------------------------------
   Modal
-------------------------------- */
interface ModalProps {
  visible: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ visible, onClose, children }) => {
  const bodyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!visible) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKey);
    const prev = document.activeElement as HTMLElement | null;

    setTimeout(() => {
      bodyRef.current
        ?.querySelector<HTMLElement>('button, a, input, [tabindex]')
        ?.focus();
    }, 0);

    return () => {
      window.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [visible, onClose]);

  if (!visible) return null;

  return (
    <ModalLayer
      className="dashboard-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="dashboard-modal apg-details-modal"
        onClick={(e) => e.stopPropagation()}
        ref={bodyRef}
      >
        <button
          className="dashboard-modal-close top-right"
          aria-label="Close modal"
          onClick={onClose}
        >
          ×
        </button>
        {children}
      </div>
    </ModalLayer>
  );
};

/* -------------------------------
   Main Page
-------------------------------- */
const ApplicationPage: React.FC = () => {
  const { account } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const user = useMemo(() => (account || {}) as Record<string, any>, [account]);
  const userType = user?.userType || '';
  const userId = user?.id;

  const [applications, setApplications] = useState<ApplicationDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedApp, setSelectedApp] = useState<ApplicationDetails | null>(null);

  const autoPer = useAutoPerPage(10);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateFilter, setDateFilter] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounced(search, 250);
  const [perPage, setPerPage] = useState<number>(autoPer);
  const [page, setPage] = useState<number>(1);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(false);

  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const s = params.get('status') || '';
    const d = params.get('date') || '';
    const q = params.get('q') || '';
    const p = parseInt(params.get('page') || '1', 10);
    const pp = parseInt(params.get('perPage') || `${autoPer}`, 10);
    const sk = (params.get('sortKey') as SortKey) || 'date';
    const sd = (params.get('sortDir') as 'asc' | 'desc') || 'desc';

    setStatusFilter(s);
    setDateFilter(d);
    setSearch(q);
    setPage(Number.isFinite(p) && p > 0 ? p : 1);
    setPerPage(Number.isFinite(pp) && pp > 0 ? pp : autoPer);
    setSortKey(sk);
    setSortDir(sd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    ['status', 'date', 'q', 'page', 'perPage', 'sortKey', 'sortDir'].forEach(key => params.delete(key));
    if (statusFilter) params.set('status', statusFilter);
    if (dateFilter) params.set('date', dateFilter);
    if (debouncedSearch) params.set('q', debouncedSearch);
    if (page > 1) params.set('page', String(page));
    if (perPage !== autoPer) params.set('perPage', String(perPage));
    if (sortKey !== 'date') params.set('sortKey', sortKey);
    if (sortDir !== 'desc') params.set('sortDir', sortDir);

    const qs = params.toString();
    const target = qs ? `?${qs}` : '';
    if (target !== location.search) {
      window.history.replaceState(null, '', target || location.pathname);
    }
  }, [
    statusFilter,
    dateFilter,
    debouncedSearch,
    page,
    perPage,
    sortKey,
    sortDir,
    autoPer,
    location.pathname,
    location.search,
  ]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const targetTag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (targetTag === 'input' || targetTag === 'textarea') return;

      if (e.key === '/') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE}/v1/api/research-applications`, {
        credentials: 'include',
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed');

      let apps: ApplicationDetails[] = (data.data || []) as ApplicationDetails[];

      if (userType === 'APPLICANT') {
        apps = apps.filter((app) => app.applicantId === userId);
      } else if (userType === 'REVIEWER' || userType === 'RESEARCH_CHAIR') {
        apps = apps.filter(
          (app) =>
            app.applicantId === userId || app.status === 'SEND_FOR_REVIEW' ||
            app.status === 'REVIEWED' ||
            app.status === 'ACCEPTED'
        );
      } else if (userType === 'DEPARTMENT') {
        const deptCode = user?.departmentCode;
        apps = apps.filter((app) => app.applicantId === userId || app.applicantDepartmentCode === deptCode);
      } else if (userType === 'FACULTY_RAP' || userType === 'FACULTY_ADMIN' || userType === 'INSTITUTE_REPRESENTATIVE') {
        const facCode = user?.facultyCode;
        apps = apps.filter((app) => app.applicantId === userId || app.applicantFacultyCode === facCode);
      }

      if (userType !== 'APPLICANT') {
        apps = apps.filter((app) => app.applicantId === userId || (app.status || '').toUpperCase() !== 'DRAFT');
      }

      setApplications(apps);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error fetching applications');
    } finally {
      setLoading(false);
    }
  }, [userType, userId, user]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(fetchApplications, 15_000);
    return () => clearInterval(id);
  }, [autoRefresh, fetchApplications]);

  const fetchDetails = async (id: number): Promise<ApplicationDetails> => {
    const query = userId !== undefined && userId !== null ? `?userId=${encodeURIComponent(String(userId))}` : '';
    const res = await fetch(`${API_BASE}/v1/api/research-applications/details/${id}${query}`, {
      credentials: 'include',
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message || 'Failed to load application details');
    return data.data as ApplicationDetails;
  };

  const clearLocalDraftRecovery = () => {
    localStorage.removeItem('app.formData');
    localStorage.removeItem('app.step');
    localStorage.removeItem('app.applicationId');
  };

  const handleNewApplication = () => {
    clearLocalDraftRecovery();
    navigate('/applications/new');
  };

  const handleDeleteDraft = async (id: number) => {
    const confirmed = window.confirm('Delete this draft permanently? Uploaded draft documents will also be removed.');
    if (!confirmed) return;
    setLoading(true);
    setError('');
    try {
      const query = userId !== undefined && userId !== null ? `?userId=${encodeURIComponent(String(userId))}` : '';
      const res = await fetch(`${API_BASE}/v1/api/research-applications/drafts/${id}${query}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to delete draft');
      setApplications((prev) => prev.filter((app) => app.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error deleting draft');
    } finally {
      setLoading(false);
    }
  };

  const openDocumentByPath = async (
    path: string | null | undefined,
    originalName: string | null | undefined,
    applicationId: number,
    fallbackName: string
  ) => {
    const fileName = originalName || getFileNameFromPath(path) || fallbackName;
    const documentType = fallbackName === 'Ethical_Clearance.pdf' ? 'ethical-clearance' : 'approval-letter';
    const urls = [`${API_BASE}/v1/api/documents/applications/${applicationId}/approval-documents/${documentType}/download?userId=${encodeURIComponent(String(userId))}`];
    await openWithCandidates(urls, fileName, 'open');
  };

  const handleDownloadApprovalLetter = async (id: number) => {
    try {
      const details = await fetchDetails(id);
      await openDocumentByPath(
        details.ethicalClearancePath,
        details.ethicalClearanceOriginalName,
        details.id,
        'Ethical_Clearance.pdf'
      );
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Unable to fetch ethics clearance letter.');
    }
  };

  const handleDownloadConfirmation = async (id: number) => {
    try {
      const details = await fetchDetails(id);
      await openDocumentByPath(
        details.approvalLetterPath,
        details.approvalLetterOriginalName,
        details.id,
        'Approval_Letter.pdf'
      );
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Unable to fetch confirmation letter.');
    }
  };

  useEffect(()=>{const id=Number(new URLSearchParams(location.search).get('highlight'));if(id>0&&userId)void handleViewApplication(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[location.search,userId]);
  const handleViewApplication = async (id: number) => {
    setLoading(true);
    setError('');

    try {
      const details = await fetchDetails(id);
      setSelectedApp(details);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error fetching application details');
    } finally {
      setLoading(false);
    }
  };

  const handleResume = async (id: number) => {
    let resumeStep: ResumeStep = 2;
    let details: ApplicationDetails | undefined;

    try {
      setLoading(true);
      setError('');
      details = await fetchDetails(id);
      resumeStep = details.revisionsRequired || details.status === 'NOT_RECOMMENDED'
        ? 1 : determineResumeStep(details);
    } catch (e) {
      console.error('Error while preparing resume:', e);
    } finally {
      setLoading(false);
      navigate(`/applications/edit/${id}?step=${resumeStep}`, {
        state: details ? { application: details, resumeStep } : { resumeStep },
      });
    }
  };

  const getAttachmentLabel = (c: AppComment) => {
    switch ((c.status || '').toUpperCase()) {
      case 'RECOMMENDED':
        return 'Faculty Rap – Faculty Meeting Minutes';
      case 'REVIEWED':
        return 'Reviewer – Signature';
      case 'SEND_FOR_REVIEW':
        return 'Research Officer – Approval Letter';
      case 'SEND_TO_FINAL':
        return 'Research Officer – Ethical Clearance Form';
      case 'ACCEPTED':
        return 'Admin – Signature';
      default:
        return c.originalFileName || '';
    }
  };

  const onSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'date' ? 'desc' : 'asc');
    }
  };

  const SortBtn: React.FC<{ active: boolean; dir: 'asc' | 'desc' }> = ({ active, dir }) => (
    <span className={`apg-sort-caret ${active ? 'active' : ''}`} aria-hidden>
      {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
    </span>
  );

  const filtered = useMemo(() => {
    let list = [...applications];
    if (new URLSearchParams(location.search).get('owner') === 'me') {
      list = list.filter(app => String(app.applicantId) === String(userId));
    }

    if (statusFilter === 'DRAFT') {
      list = list.filter(
        (app) =>
          (app.status || '').toUpperCase() === 'DRAFT' &&
          app.applicantId === userId
      );
    } else if (statusFilter) {
      list = list.filter(
        (app) => (app.status || '').toUpperCase() === statusFilter.toUpperCase()
      );
    }

    if (dateFilter) {
      list = list.filter((app) => {
        const rawDate = statusFilter === 'DRAFT'
          ? app.draftLastUpdatedAt || app.createdAt || app.stageOneSubmittedAt
          : app.stageOneSubmittedAt;
        if (!rawDate) return false;
        const date = new Date(rawDate);
        return date.toISOString().slice(0, 10) === dateFilter;
      });
    }

    if (debouncedSearch.trim()) {
      const q = debouncedSearch.toLowerCase();
      list = list.filter(
        (app) =>
          (app.researchTitle || '').toLowerCase().includes(q) ||
          (app.researchDescription || '').toLowerCase().includes(q) ||
          `${app.applicantName || ''} ${app.applicantSurname || ''}`.toLowerCase().includes(q)
      );
    }

    const acceptedFirst = (a: ApplicationDetails, b: ApplicationDetails) => {
      const aa = (a.status || '').toUpperCase() === 'ACCEPTED' ? 1 : 0;
      const bb = (b.status || '').toUpperCase() === 'ACCEPTED' ? 1 : 0;
      if (aa !== bb) return bb - aa;
      return 0;
    };

    list.sort((a, b) => {
      const top = acceptedFirst(a, b);
      if (top !== 0) return top;

      if (sortKey === 'title') {
        const av = (a.researchTitle || '').toLowerCase();
        const bv = (b.researchTitle || '').toLowerCase();
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }

      if (sortKey === 'applicant') {
        const av = (statusFilter === 'DRAFT'
          ? a.draftReference || `DRAFT-${a.id}`
          : `${a.applicantName || ''} ${a.applicantSurname || ''}`).toLowerCase();
        const bv = (statusFilter === 'DRAFT'
          ? b.draftReference || `DRAFT-${b.id}`
          : `${b.applicantName || ''} ${b.applicantSurname || ''}`).toLowerCase();
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }

      if (sortKey === 'status') {
        const av = statusFilter === 'DRAFT' ? a.applicationType || '' : statusBadgeLabel(a.status);
        const bv = statusFilter === 'DRAFT' ? b.applicationType || '' : statusBadgeLabel(b.status);
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }

      if (sortKey === 'progress') {
        const av = displayedProgress(a);
        const bv = displayedProgress(b);
        return sortDir === 'asc' ? av - bv : bv - av;
      }

      const av = +(a.draftLastUpdatedAt || a.stageOneSubmittedAt || a.createdAt ? new Date(a.draftLastUpdatedAt || a.stageOneSubmittedAt || a.createdAt || '') : 0);
      const bv = +(b.draftLastUpdatedAt || b.stageOneSubmittedAt || b.createdAt ? new Date(b.draftLastUpdatedAt || b.stageOneSubmittedAt || b.createdAt || '') : 0);
      return sortDir === 'asc' ? av - bv : bv - av;
    });

    return list;
  }, [applications, statusFilter, dateFilter, debouncedSearch, sortKey, sortDir, userType, userId, location.search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(page, totalPages);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  const pageSlice = useMemo(
    () => filtered.slice((safePage - 1) * perPage, safePage * perPage),
    [filtered, perPage, safePage]
  );

  const selectedResearchDesign = useMemo(
    () => getSelectedFlags(selectedApp?.researchDesign),
    [selectedApp]
  );

  const selectedResearchTools = useMemo(
    () => getSelectedFlags(selectedApp?.researchTool),
    [selectedApp]
  );

  if (loading && !applications.length) {
    return (
      <div className="app-application-page">
        <div className="dashboard-loading">
          <div className="skeleton-row" />
          <div className="skeleton-row" />
          <div className="skeleton-row" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-application-page">
        <p className="error-msg">{error}</p>
        <div style={{ marginTop: 12 }}>
          <button className="app-view-btn" onClick={() => fetchApplications()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const empty = filtered.length === 0;
  const isDraftMode = statusFilter === 'DRAFT';

  return (
    <main className="app-application-page" tabIndex={-1}>
      <section className="app-howitworks" aria-labelledby="howitworks-title">
        <header className="howitworks-head">
          <h2 id="howitworks-title" className="howitworks-title">
            How it Works
          </h2>
          <div className="autorefresh">
            <label className="toggle">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              <span>Auto-refresh</span>
            </label>
          </div>
        </header>

        <div className="howitworks-flow" aria-label="Application process flow" role="list">
          <div className="flow-step" role="listitem">
            <span className="flow-icon" aria-hidden>📝</span>
            <span>Application</span>
          </div>
          <span className="flow-arrow" aria-hidden>→</span>
          <div className="flow-step" role="listitem">
            <span className="flow-icon" aria-hidden>🏢</span>
            <span>Dept. Recommendation</span>
          </div>
          <span className="flow-arrow" aria-hidden>→</span>
          <div className="flow-step" role="listitem">
            <span className="flow-icon" aria-hidden>🏫</span>
            <span>Faculty Recommendation</span>
          </div>
          <span className="flow-arrow" aria-hidden>→</span>
          <div className="flow-step" role="listitem">
            <span className="flow-icon" aria-hidden>👨‍🔬</span>
            <span>Chair Recommendation</span>
          </div>
          <span className="flow-arrow" aria-hidden>→</span>
          <div className="flow-step" role="listitem">
            <span className="flow-icon" aria-hidden>🏛️</span>
            <span>Directorate Approval</span>
          </div>
        </div>
      </section>

      <section className="app-headerCard">
        <h1 className="app-heading">Applications</h1>
        <div className="app-header-cta">
          <button
            className="app-newButton"
            aria-label="Start a new application"
            onClick={handleNewApplication}
          >
            New Application
          </button>
        </div>
      </section>

      <StatCards items={[
        {label:'Accessible applications',value:applications.length,total:applications.length,meterLabel:'Authorized portfolio'},
        {label:'Drafts',value:applications.filter(a=>a.status==='DRAFT').length,total:applications.length,meterLabel:'Of accessible applications'},
        {label:'Matching filters',value:filtered.length,total:applications.length,meterLabel:'Current search results'}
      ]}/>
      <section className="app-filter-bar" role="region" aria-label="Filters">
        <div className="filters-left">
          <label htmlFor="statusFilter">
            Status{' '}
            <select
              id="statusFilter"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          <label htmlFor="dateFilter" className="date-filter">
            Date{' '}
            <input
              id="dateFilter"
              type="date"
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value);
                setPage(1);
              }}
              max={new Date().toISOString().slice(0, 10)}
            />
          </label>

          {(statusFilter || dateFilter) && (
            <button
              className="app-clear-filter-btn"
              aria-label="Clear filters"
              onClick={() => {
                setStatusFilter('');
                setDateFilter('');
                setPage(1);
              }}
            >
              Clear
            </button>
          )}
        </div>

        <div className="filters-right">
          <div className="app-search-bar">
            <input
              type="search"
              ref={searchInputRef}
              className="app-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title, description or applicant"
              aria-label="Search applications"
              autoComplete="off"
              onKeyDown={(e) => {
                if (e.key === 'Escape') setSearch('');
              }}
            />
            <span className="search-hint" aria-hidden>
              Press “/” to search
            </span>
          </div>

          <label className="perpage">
            Rows{' '}
            <select
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(1);
              }}
              aria-label="Rows per page"
            >
              {[autoPer, 10, 25, 50]
                .filter((v, i, a) => a.indexOf(v) === i)
                .sort((a, b) => a - b)
                .map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
            </select>
          </label>
        </div>
      </section>

      <div
        className="app-table-container"
        tabIndex={0}
        role="region"
        aria-label="Applications table"
      >
        <table className="app-table apg-table">
          <thead>
            <tr>
              <th
                onClick={() => onSort('applicant')}
                className="th-sort"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onSort('applicant')}
              >
                {isDraftMode ? 'Draft Reference' : 'Applicant'} <SortBtn active={sortKey === 'applicant'} dir={sortDir} />
              </th>

              <th
                onClick={() => onSort('title')}
                className="th-sort"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onSort('title')}
              >
                Research Title <SortBtn active={sortKey === 'title'} dir={sortDir} />
              </th>

              <th
                onClick={() => onSort('status')}
                className="th-sort"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onSort('status')}
              >
                {isDraftMode ? 'Application Type' : 'Status'} <SortBtn active={sortKey === 'status'} dir={sortDir} />
              </th>

              <th
                onClick={() => onSort('date')}
                className="th-sort"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onSort('date')}
              >
                {isDraftMode ? 'Last Updated' : 'Date Applied'} <SortBtn active={sortKey === 'date'} dir={sortDir} />
              </th>

              <th
                onClick={() => onSort('progress')}
                className="th-sort"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onSort('progress')}
              >
                Progress <SortBtn active={sortKey === 'progress'} dir={sortDir} />
              </th>

              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {empty ? (
              <tr>
                <td colSpan={6} className="no-results">
                  <div className="no-apps-illustration" aria-label="No applications found" />
                  <p>No applications match your filters.</p>
                  {userId != null && (
                    <button className="app-newButton" onClick={handleNewApplication}>
                      Start a new application
                    </button>
                  )}
                </td>
              </tr>
            ) : (
              pageSlice.map((app) => {
                const pct = displayedProgress(app);
                const isAccepted = (app.status || '').toUpperCase() === 'ACCEPTED';
                const isReturned = (app.status || '').toUpperCase() === 'NOT_RECOMMENDED';
                const isDraftOrNew =
                  app.applicantId === userId &&
                  (!app.status || (app.status || '').toUpperCase() === 'DRAFT');
                const canRevise =
                  app.applicantId === userId && (isReturned || app.editableByApplicant === true);

                return (
                  <tr key={app.id} className={isAccepted ? 'apg-row-accepted' : ''}>
                    <td className="apg-applicant-name">
                      {isDraftMode
                        ? safeText(app.draftReference || `DRAFT-${app.id}`)
                        : `${safeText(app.applicantName)} ${safeText(app.applicantSurname)}`.replace('â€” â€”', 'â€”')}
                    </td>

                    <td className="cell-title">
                      <ApplicationFlags flags={app.flags} />
                      <div className="title-wrap">
                        <span className="title-text">{app.researchTitle || 'Untitled Application'}</span>
                      </div>
                    </td>

                    <td className="apg-status-cell">
                      {isDraftMode ? safeText(app.applicationType || 'Not selected') : (
                        <span className={statusBadgeClass(app.status)}>
                          {statusBadgeLabel(app.reviewOutcome || app.status)}
                        </span>
                      )}
                    </td>

                    <td>{formatDate(isDraftMode ? (app.draftLastUpdatedAt || app.createdAt) : app.stageOneSubmittedAt)}</td>

                    <td>
                      {isReturned ? <span>Awaiting applicant corrections</span> : <LinearProgress value={pct} compact label />}
                    </td>

                    <td>
                      {isDraftOrNew || canRevise ? (
                        <div className="apg-accepted-actions">
                          <button
                            className="app-view-btn"
                            aria-label={isReturned ? 'Revise Application' : 'Resume Application'}
                            onClick={() => handleResume(app.id)}
                          >
                            {isReturned ? 'Revise' : 'Resume'}
                          </button>
                          {isDraftMode ? (
                            <button
                              className="app-download-btn apg-btn-approval"
                              aria-label="Delete Draft"
                              onClick={() => handleDeleteDraft(app.id)}
                            >
                              Delete
                            </button>
                          ) : null}
                        </div>
                      ) : isAccepted ? (
                        <div className="apg-accepted-actions">
                          <button
                            className="app-download-btn apg-btn-confirmation"
                            aria-label="Open Confirmation of Approval"
                            onClick={() => handleDownloadConfirmation(app.id)}
                          >
                            Confirmation of Approval
                          </button>
                          <button
                            className="app-download-btn apg-btn-approval"
                            aria-label="Open Ethics Clearance Letter"
                            onClick={() => handleDownloadApprovalLetter(app.id)}
                          >
                            Ethics Clearance Letter
                          </button>
                        </div>
                      ) : (
                        <button
                          className="app-view-btn"
                          aria-label="View Application"
                          onClick={() => handleViewApplication(app.id)}
                        >
                          View
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <nav className="app-pagination-controls" aria-label="Pagination">
        <span className="pg-text">
          Page{' '}
          <button
            disabled={safePage === 1}
            aria-label="Previous Page"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </button>{' '}
          {safePage} / {totalPages}{' '}
          <button
            disabled={safePage === totalPages}
            aria-label="Next Page"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </span>

        <span className="pg-count" aria-live="polite">
          {filtered.length} result{filtered.length === 1 ? '' : 's'}
        </span>
      </nav>

      {selectedApp && (
        <Modal visible={true} onClose={() => setSelectedApp(null)}>
          <div className="apg-modal-head">
            <h2 className="app-modal-title">{safeText(selectedApp.researchTitle)}</h2>
            <div className="apg-status-inline">
              <strong>Status:</strong>{' '}
              <span className={statusBadgeClass(selectedApp.status)}>
                {statusBadgeLabel(selectedApp.reviewOutcome || selectedApp.status)}
              </span>
            </div>
          </div>

          <div style={{ margin: '8px 0 16px' }}>
            <LinearProgress value={displayedProgress(selectedApp)} label />
          </div>

          <div className="apg-details-sections">
            <section className="apg-detail-card">
              <h3>Applicant Details</h3>
              <p>Applicant role: {roleLabel(selectedApp.applicantInstitutionalRole)}</p>
              <div className="apg-detail-grid">
                <DetailRow
                  label="Applicant ID"
                  value={safeText(selectedApp.applicantId)}
                />
                <DetailRow
                  label="Full Name"
                  value={`${safeText(selectedApp.applicantName)} ${safeText(
                    selectedApp.applicantSurname
                  )}`.replace('— —', '—')}
                />
                <DetailRow
                  label="Faculty Code"
                  value={safeText(selectedApp.applicantFacultyCode)}
                />
                <DetailRow
                  label="Department Code"
                  value={safeText(selectedApp.applicantDepartmentCode)}
                />
              </div>
            </section>

            <section className="apg-detail-card">
              <h3>Application Summary</h3>
              <div className="apg-detail-grid">
                <DetailRow label="Application ID" value={selectedApp.id} />
                <DetailRow
                  label="Date Applied"
                  value={formatDateTime(selectedApp.stageOneSubmittedAt)}
                />
                <DetailRow
                  label="Stage Four Submitted"
                  value={formatDateTime(selectedApp.stageFourSubmittedAt)}
                />
                <DetailRow
                  label="Declaration Accepted"
                  value={humanizeBool(selectedApp.declarationAccepted)}
                />
                <DetailRow
                  label="Proposal Defended"
                  value={humanizeBool(selectedApp.proposalDefended)}
                />
                <DetailRow
                  label="Fieldwork"
                  value={humanizeBool(selectedApp.fieldwork)}
                />
                <DetailRow
                  label="Category of Participants"
                  value={safeText(selectedApp.categoryOfParticipants)}
                />
                <DetailRow
                  label="Level of Sensitivity"
                  value={safeText(selectedApp.levelOfSensitivity)}
                />
                <DetailRow
                  label="Research Location"
                  value={safeText(selectedApp.researchLocation)}
                />
                <DetailRow
                  label="Funding of Research"
                  value={safeText(selectedApp.fundingOfResearch)}
                />
              </div>
            </section>

            {selectedApp.revisionsRequired && (
              <section className="apg-detail-card">
                <h3>Revisions Required</h3>
                <div className="apg-detail-grid">
                  <DetailRow
                    label="Returned From Stage"
                    value={safeText(selectedApp.returnedWorkflowStageName || selectedApp.returnedWorkflowStageCode)}
                  />
                  <DetailRow
                    label="Decision Date"
                    value={formatDateTime(selectedApp.revisionDecisionAt || selectedApp.returnedForCorrectionAt)}
                  />
                  <DetailRow
                    label="Revision Cycle"
                    value={selectedApp.revisionNumber ?? '—'}
                  />
                  <DetailRow
                    label="Latest Resubmission"
                    value={formatDateTime(selectedApp.resubmittedAt)}
                  />
                  <DetailRow
                    label="Revision Remarks"
                    value={<div style={{ whiteSpace: 'pre-wrap' }}>{safeText(selectedApp.revisionRemarks)}</div>}
                  />
                  <DetailRow
                    label="Decision Attachment"
                    value={
                      selectedApp.revisionDecisionAttachmentPath ? (
                        <button
                          type="button"
                          className="apg-link"
                          onClick={async () => {
                            try {
                              await openWithCandidates(
                                buildFileCandidates(selectedApp.revisionDecisionAttachmentPath, selectedApp.id),
                                selectedApp.revisionDecisionAttachmentName || 'Decision attachment',
                                'open'
                              );
                            } catch (e) {
                              alert(
                                e instanceof Error
                                  ? e.message
                                  : 'Unable to open decision attachment.'
                              );
                            }
                          }}
                        >
                          {selectedApp.revisionDecisionAttachmentName || 'Open attachment'}
                        </button>
                      ) : (
                        '—'
                      )
                    }
                  />
                </div>
              </section>
            )}

            <section className="apg-detail-card">
              <h3>Workflow Timeline</h3>
              <ApplicationJourney applicationId={selectedApp.id} onChanged={journey => {
                  setSelectedApp(current => current ? { ...current, flags: journey.flags, reviewOutcome: journey.reviewOutcome } : current);
                  setApplications(current => current.map(app => app.id === journey.applicationId ? { ...app, flags: journey.flags, reviewOutcome: journey.reviewOutcome } : app));
                }} />
            </section>

            <section className="apg-detail-card">
              <h3>Research Details</h3>
              <div className="apg-detail-grid apg-detail-grid-single">
                <DetailRow
                  label="Research Title"
                  value={safeText(selectedApp.researchTitle)}
                />
                <DetailRow
                  label="Research Description"
                  value={
                    <div style={{ whiteSpace: 'pre-wrap' }}>
                      {safeText(selectedApp.researchDescription)}
                    </div>
                  }
                />
                <DetailRow
                  label="Relevant Experience"
                  value={
                    <div style={{ whiteSpace: 'pre-wrap' }}>
                      {safeText(selectedApp.relevantExperience)}
                    </div>
                  }
                />
                <DetailRow
                  label="Co-Researchers Details"
                  value={
                    <div style={{ whiteSpace: 'pre-wrap' }}>
                      {safeText(selectedApp.coResearchersDetails)}
                    </div>
                  }
                />
                <DetailRow
                  label="Academic Qualifications"
                  value={
                    <div style={{ whiteSpace: 'pre-wrap' }}>
                      {safeText(selectedApp.academicQualifications)}
                    </div>
                  }
                />
                <DetailRow
                  label="Proposed Fieldwork"
                  value={
                    <div style={{ whiteSpace: 'pre-wrap' }}>
                      {safeText(selectedApp.proposedFieldwork)}
                    </div>
                  }
                />
                <DetailRow
                  label="Fieldwork Details"
                  value={
                    <div style={{ whiteSpace: 'pre-wrap' }}>
                      {safeText(selectedApp.fieldworkDetails)}
                    </div>
                  }
                />
              </div>
            </section>

            <section className="apg-detail-card">
              <h3>Research Design</h3>
              {selectedResearchDesign.length ? (
                <ul className="apg-pill-list">
                  {selectedResearchDesign.map((item) => (
                    <li key={item} className="apg-pill">
                      {item}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="apg-muted">No research design selected.</p>
              )}
            </section>

            <section className="apg-detail-card">
              <h3>Research Tools</h3>
              {selectedResearchTools.length ? (
                <ul className="apg-pill-list">
                  {selectedResearchTools.map((item) => (
                    <li key={item} className="apg-pill">
                      {item}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="apg-muted">No research tools selected.</p>
              )}
            </section>

            <section className="apg-detail-card">
              <h3>Documents</h3>
              <DocumentList
                documents={selectedApp.documents || []}
                applicationId={selectedApp.id}
                userId={userId}
              />
            </section>

            <section className="apg-detail-card">
              <h3>Comments / Workflow Trail</h3>
              {selectedApp.comments?.length ? (
                <div className="apg-table-scroll" tabIndex={0} aria-label="Comments and workflow trail table">
                  <table className="app-modal-comments-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Comment</th>
                        <th>Next Recommender</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th>Attachment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedApp.comments.map((c) => {
                        const pathOrName = c.filePath || c.originalFileName;
                        const urls = buildWorkflowAttachmentUrls(c, selectedApp.id, userId);

                        const label =
                          c.originalFileName ||
                          getFileNameFromPath(c.filePath) ||
                          getAttachmentLabel(c) ||
                          getFileNameFromPath(pathOrName) ||
                          'Attachment';

                        return (
                          <tr key={c.id}>
                            <td>{c.id}</td>
                            <td style={{ whiteSpace: 'pre-wrap' }}>{safeText(c.comment)}</td>
                            <td>
                              <div>{safeText(c.createdByUserName || c.nextApproverName)}</div>
                              {/* {c.nextApproverName ? (
                                <small className="apg-muted">
                                  Next Recommender: {c.nextApproverName}
                                </small>
                              ) : null} */}
                            </td>
                            <td>
                              <span className={statusBadgeClass(c.status)}>
                                {statusBadgeLabel(c.status)}
                              </span>
                            </td>
                            <td>{formatDateTime(c.createdAt)}</td>
                            <td>
                              {urls.length ? (
                                <button
                                  type="button"
                                  className="apg-link"
                                  onClick={async () => {
                                    try {
                                      await openWithCandidates(urls, label, 'open');
                                    } catch (e) {
                                      alert(
                                        e instanceof Error
                                          ? e.message
                                          : 'Unable to open attachment.'
                                      );
                                    }
                                  }}
                                >
                                  {label}
                                </button>
                              ) : (
                                '—'
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="apg-muted">No comments found.</p>
              )}
            </section>
          </div>

          <div className="view-section">
            <button
              className="app-view-btn"
              aria-label="Close modal"
              onClick={() => setSelectedApp(null)}
            >
              Close
            </button>
          </div>
        </Modal>
      )}
    </main>
  );
};

export default ApplicationPage;
